/**
 * Diagnostic AI Types
 * Types for the chain-of-debate diagnostic system.
 */

/** Diagnostic pipeline stages */
export type DiagnosticPipelineStage =
  | 'safety-screening'
  | 'understanding'
  | 'hypothesis-generation'
  | 'adversarial-review'
  | 'checklist-verification'
  | 'consensus'
  | 'response-generation'
  | 'complete';

/** Triage severity levels */
export type TriageSeverity = 'green' | 'yellow' | 'orange' | 'red';

/** Human-readable labels for triage severity */
export const TRIAGE_LABELS: Record<TriageSeverity, string> = {
  green: 'Non-urgent - Self-care may be appropriate',
  yellow: 'See your doctor within a few days',
  orange: 'See your doctor within 24 hours',
  red: 'Seek immediate medical attention',
};

/** Colors for triage severity */
export const TRIAGE_COLORS: Record<TriageSeverity, string> = {
  green: '#22c55e',
  yellow: '#eab308',
  orange: '#f97316',
  red: '#ef4444',
};

/** Pipeline stage labels */
export const DIAGNOSTIC_STAGE_LABELS: Record<DiagnosticPipelineStage, string> = {
  'safety-screening': 'Checking for emergencies...',
  'understanding': 'Understanding your symptoms...',
  'hypothesis-generation': 'Analyzing possible conditions...',
  'adversarial-review': 'Verifying analysis...',
  'checklist-verification': 'Checking completeness...',
  'consensus': 'Forming assessment...',
  'response-generation': 'Preparing response...',
  'complete': 'Complete',
};

/** Differential diagnosis entry */
export interface DifferentialDiagnosisEntry {
  condition: string;
  confidence: number;
  evidenceFor: string[];
  evidenceAgainst: string[];
  requiresTests: string[];
}

/** Extracted symptom */
export interface ExtractedSymptom {
  name: string;
  severity?: 'mild' | 'moderate' | 'severe';
  duration?: string;
  onset?: 'sudden' | 'gradual';
  location?: string;
  character?: string;
}

/** SSE event from diagnostic endpoint */
export interface DiagnosticSSEEvent {
  event_type: 'stage' | 'content' | 'diagnosis_update' | 'triage_update' | 'symptom_update' | 'red_flag' | 'emergency' | 'suggestion' | 'done' | 'error';
  data: Record<string, any>;
}

/** Diagnostic session state (frontend) */
export interface DiagnosticSessionState {
  sessionId: string | null;
  status: 'idle' | 'starting' | 'active' | 'completed' | 'emergency' | 'error';
  triageSeverity: TriageSeverity | null;
  differentialDiagnosis: DifferentialDiagnosisEntry[];
  extractedSymptoms: ExtractedSymptom[];
  redFlagsDetected: string[];
  recommendedTests: Array<{ name: string; reason: string; urgency: string }>;
  exchangeCount: number;
  maxExchanges: number;
  currentPipelineStage: DiagnosticPipelineStage | null;
  consentGiven: boolean;
}

/** Initial diagnostic state */
export const INITIAL_DIAGNOSTIC_STATE: DiagnosticSessionState = {
  sessionId: null,
  status: 'idle',
  triageSeverity: null,
  differentialDiagnosis: [],
  extractedSymptoms: [],
  redFlagsDetected: [],
  recommendedTests: [],
  exchangeCount: 0,
  maxExchanges: 20,
  currentPipelineStage: null,
  consentGiven: false,
};

/** Start diagnostic request */
export interface DiagnosticStartRequest {
  initial_complaint: string;
  health_context?: Record<string, any>;
}

/** Start diagnostic response */
export interface DiagnosticStartResponse {
  session_id: string;
  status: string;
  message: string;
  suggestions: string[];
  triage_severity: string | null;
  pipeline_stages: string[];
}

/** Message request */
export interface DiagnosticMessageRequest {
  session_id: string;
  message: string;
}

/** Session response */
export interface DiagnosticSessionResponse {
  id: string;
  user_id: string;
  status: string;
  triage_severity: string | null;
  exchange_count: number;
  chief_complaint: string | null;
  differential_diagnosis: DifferentialDiagnosisEntry[];
  extracted_symptoms: ExtractedSymptom[];
  red_flags_detected: string[];
  recommended_tests: any[];
  started_at: string;
  ended_at: string | null;
  summary: string | null;
  final_recommendations: any | null;
}
